import React, { useState, useContext, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react'; 
import { Box, Typography, useMediaQuery, useTheme, Skeleton } from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query'; 
import { useLocation } from 'react-router-dom';
import AuthContext from "./auth/Context";
import { useQuickAction } from './DataQuickActionContext'; 
import LockScreen from "./LockScreen";
import useAuthGate from "./useAuthGate";
import ReportHeader  from "./ReportHeader"; 
import FilterDrawer  from "./ReportFilterDrawer"; 
import ReportColumnDrawer from './ReportColumnDrawer';
import useScrollRestoration from './useScrollRestoration'; 
import { getDateRangeByType } from './DateFilterInput';
import ReportDataViewer from './ReportDataViewer';
import PrintActionDrawer from './PrintActionDrawer';
import DataPivotViewer from './DataPivotViewer';
import * as utils from "../utils";

import createDefaults from './createDefaults'; 
import moment from 'moment';
import {numeral } from "../utils";

const hideConditionCache = new Map();
const isHiddenColumn = (col, condition={}) => {
    if (col.hide) return true;
    if (col.hide_with_condition) {
        try {
            let func_string = col.hide_with_condition;
            if (!hideConditionCache.has(func_string)) {
                let formatted_string = func_string.indexOf("return ") < 0 ? "return " + func_string : func_string;
                const compiledFunc = new Function("condition", "moment", "numeral", formatted_string);
                hideConditionCache.set(func_string, compiledFunc);
            }
            const func = hideConditionCache.get(func_string);
            return func(condition||{}, moment, numeral); 
        } catch(e) {
            console.error("[ReportFrame] Lỗi khi chạy script ẩn cột:", col.field, e,condition);
            return false;
        }
    }
    return false;
}
const apiCache = new Map();

// =====================================================================
// HÀM TIỆN ÍCH LỌC PARAMETER DIALOG RA KHỎI CHUỖI SEARCH
// =====================================================================
const getCleanSearchString = (search) => {
  if (!search) return "";
  const params = new URLSearchParams(search);
  const keysToDelete = [];
  for (const key of params.keys()) {
      if (key.startsWith('dialog_')) keysToDelete.push(key);
  }
  keysToDelete.forEach(key => params.delete(key));
  const newSearch = params.toString();
  return newSearch ? `?${newSearch}` : "";
};

// =====================================================================
// COMPONENT RENDER SKELETON (KHUNG XƯƠNG TẢI DỮ LIỆU)
// =====================================================================
const ReportSkeleton = ({ viewMode, isWidget }) => {
  if (isWidget) {
    return (
      <Box sx={{ p: 2}}>
        <Skeleton variant="text" width="40%" height={24} sx={{ mb: 1.5 }} />
        <Skeleton variant="rounded" height={48} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" height={48} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" height={48} />
      </Box>
    );
  }

  // Dạng thẻ (Card) dành cho Mobile
  if (viewMode === 'card') {
    return (
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {[1, 2, 3, 4, 5].map((item) => (
          <Box key={item} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Skeleton variant="text" width="50%" height={28} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="90%" height={20} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width="70%" height={20} />
          </Box>
        ))}
      </Box>
    );
  }

  // Dạng bảng (Table) hoặc Pivot
  return (
    <Box sx={{ p: 2, width: '100%' }}>
      {/* Giả lập Header bảng */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, borderBottom: '2px solid', borderColor: 'divider', pb: 1 }}>
        <Skeleton variant="text" width="10%" height={28} />
        <Skeleton variant="text" width="30%" height={28} />
        <Skeleton variant="text" width="20%" height={28} />
        <Skeleton variant="text" width="20%" height={28} />
        <Skeleton variant="text" width="20%" height={28} />
      </Box>
      {/* Giả lập các Dòng dữ liệu */}
      {[1, 2, 3, 4, 5, 6, 7].map((row) => (
        <Box key={row} sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
          <Skeleton variant="text" width="10%" height={24} />
          <Skeleton variant="text" width="30%" height={24} />
          <Skeleton variant="text" width="20%" height={24} />
          <Skeleton variant="text" width="20%" height={24} />
          <Skeleton variant="text" width="20%" height={24} />
        </Box>
      ))}
    </Box>
  );
};


// =====================================================================
// COMPONENT RENDER CHÍNH
// =====================================================================
const ReportFrameRender = forwardRef((props, ref) => {
  const {
    location, 
    code, apiCode,              
    title,     
    searchLabel,
    cancelLabel,
    filterFields,     
    displayFields,    
    onBack,                
    renderReport,    
    renderCardItem,   
    renderTableRow,      
    isWidget = false,             
    defaultCondition = {},        
    refreshInterval = 0,          
    processData,           
    enableLoadMore,       
    defaultLimit = 50,    
    contentSx = {},  
    containerSx = {},      
    requireLogin = false,
    drillDownCode,
    drillDownScript,
    handleDataScript,
    onDrillDown,
    drillDownCondition,
    handleConditionScript,
    // Mở từ widget dashboard (moBaoCaoTuDashboard.js): chế độ xem + mẫu pivot ban đầu.
    initialViewMode,
    initialPivotConfigId
  } = props;

  const context = useContext(AuthContext);
  const { openReport,openEdit } = useQuickAction(); 
  
  const reportConfig = context.config?.getReportConfig ? context.config.getReportConfig(code) : {};

  const finalTitle = title || reportConfig?.title || "Báo cáo";
  const finalFilterFields = (filterFields && filterFields.length > 0) ? filterFields : (reportConfig?.filterFields || []);
  const finalDisplayFields = (displayFields && displayFields.length > 0) ? displayFields : (reportConfig?.displayFields || reportConfig?.columns || []);
  const finalProcessData = processData || reportConfig?.processData;
  const finalEnableLoadMore = enableLoadMore !== undefined ? enableLoadMore : (reportConfig?.enableLoadMore ?? true);
  const finalDrillDownCode = drillDownCode || reportConfig?.drillDownCode;
  const finalDrillDownScript = drillDownScript || reportConfig?.drillDownScript || reportConfig?.drilldown_info;

  //const isPublicUser = !context.userInfo || context.userInfo?.email === 'public';
  const userToken = context.userInfo?.token || (typeof context.getToken === 'function' ? context.getToken() : '') || context.config?.public_token;
  // Trạng thái chắn màn hình + có được phép gọi API chưa (xem ./useAuthGate).
  // `error` được truyền vào sau khi useInfiniteQuery khai báo, nên gọi hook 2 lần: lần này để lấy
  // canFetch cho `enabled`, lần sau (authGate) để lấy trạng thái chắn có tính cả lỗi 401.
  const authReady = useAuthGate(requireLogin);
  
  const finalApiCode = apiCode || code;

  // === TẠO KEY LƯU TRỮ CHO LOCALSTORAGE ===
  const userKey = context.userInfo?.email || 'public';
  const filterStorageKey = useMemo(() => `flexbiz_report_filters_${userKey}_${code}`, [userKey, code]);
  const viewModeStorageKey = useMemo(() => `flexbiz_report_viewmode_${userKey}_${code}`, [userKey, code]);

  const cleanSearch = useMemo(() => getCleanSearchString(location.search), [location.search]);


 const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [currentLimit, setCurrentLimit] = useState(defaultLimit || 50);
  const [isDefaultsReady, setIsDefaultsReady] = useState(false);
  const [resolvedDefaults, setResolvedDefaults] = useState({});
  const [initialMergedData, setInitialMergedData] = useState({});
  const [appliedCondition, setAppliedCondition] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  const [currentDrillDownCondition,setCurrentDrillDownCondition] = useState(drillDownCondition);

  // ================= 2. HÀM LẤY DỮ LIỆU TỪ URL =================
  const getUrlFilters = () => {
    if (isWidget) return {}; 
    const params = new URLSearchParams(cleanSearch);
    let urlPrefill = {};
    (finalFilterFields || []).forEach(fieldDef => {
        if (fieldDef.type === 'DateFilter') {
            const fromKey = fieldDef.mapTo?.[0] || 'fromDate';
            const toKey = fieldDef.mapTo?.[1] || 'toDate';
            const fromVal = params.get(fromKey);
            const toVal = params.get(toKey);
            if (fromVal || toVal) urlPrefill[fieldDef.field] = { type: 'custom', [fromKey]: fromVal ? fromVal.substring(0, 10) : '', [toKey]: toVal ? toVal.substring(0, 10) : '' };
            return; 
        }
        const key = fieldDef.field;
        if (params.has(key)) {
            const allVals = params.getAll(key);
            const parseVal = (v) => {
                if (fieldDef.type === 'Number') return Number(v);
                if (fieldDef.type === 'Boolean') return (v === 'true' || v === '1');
                if (fieldDef.type === 'Date' && typeof v === 'string') return v.substring(0, 10);
                return v;
            };
            if (allVals.length > 1) urlPrefill[key] = allVals.map(parseVal);
            else urlPrefill[key] = parseVal(allVals[0]);
        }
    });
    return urlPrefill;
  };

  const checkHasAllRequiredFields = (dataToCheck) => {
      let isValid = true;
      (finalFilterFields || []).forEach(f => {
          if (f.required) {
              const val = dataToCheck[f.field];
              if (val === undefined || val === null || val === '') isValid = false;
              else if (Array.isArray(val) && val.length === 0) isValid = false;
          }
      });
      return isValid;
  };

  // ================= 3. STATE KHỞI TẠO VÀ XỬ LÝ DEFAULT ASYNC =================
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // TỰ ĐỘNG ĐỌC VIEW MODE TỪ LOCALSTORAGE KHI KHỞI TẠO
  // Bấm lại nút "Giao diện tuỳ chỉnh" trên header khi đang ở chế độ đó => mở trình soạn thảo.
  // Dùng bộ đếm làm tín hiệu để mỗi lần bấm đều kích hoạt lại.
  const [openCustomViewEditor, setOpenCustomViewEditor] = useState(0);
  const [viewMode, setViewMode] = useState(() => {
      // Mở từ dashboard: đúng chế độ widget đang hiện (pivot), KHÔNG ghi đè chế độ đã nhớ của người dùng.
      if (!isWidget && initialViewMode) return initialViewMode;
      if (!isWidget) {
          try {
              const storedMode = localStorage.getItem(`flexbiz_report_viewmode_${userKey}_${code}`);
              if (storedMode) return storedMode;
          } catch (e) {
              console.error("Lỗi đọc viewMode từ localStorage:", e);
          }
      }
      return isMobile ? "card" : 'table';
  });

  // HÀM BỌC ĐỂ LƯU VIEW MODE MỖI KHI THAY ĐỔI
  const handleViewModeChange = (mode) => {
      setViewMode(mode);
      if (!isWidget) {
          try {
              localStorage.setItem(viewModeStorageKey, mode);
          } catch (e) {
              console.error("Lỗi lưu viewMode vào localStorage:", e);
          }
      }
  };



  const initFingerprint = JSON.stringify({
      fields: (finalFilterFields || []).map(f => f.field), 
      defaults: defaultCondition,
      search: cleanSearch 
  });

  useEffect(() => {
    let isMounted = true;

    const initDefaults = async () => {
      const tempDefaults = {};
      try {
         await createDefaults(tempDefaults, finalFilterFields, context);
      } catch (e) {
         console.error("Lỗi khi chạy createDefaults:", e);
      }

      (finalFilterFields || []).forEach(f => {
         let val = tempDefaults[f.field] !== undefined ? tempDefaults[f.field] : f.default;

         if (val !== undefined && val !== null) {
             if (f.type === 'Date') {
                 if (val instanceof Date) val = moment(val).format('YYYY-MM-DD');
                 else if (typeof val === 'string' && val.length >= 10) val = val.substring(0, 10);
             } 
             else if (f.type === 'DateTime') {
                 if (val instanceof Date) val = val.toISOString(); 
             } 
             else if (f.type === 'DateFilter' && val && typeof val === 'object') {
                 const fromKey = f.mapTo?.[0] || 'fromDate';
                 const toKey = f.mapTo?.[1] || 'toDate';
                 let from = val[fromKey];
                 let to = val[toKey];
                 if (val.type && val.type !== 'custom' && val.type !== 'all') {
                     const range = getDateRangeByType(val.type);
                     from = range.fromDate;
                     to = range.toDate;
                 }
                 const parseDateObj = (d) => {
                     if (d instanceof Date) return moment(d).format('YYYY-MM-DD');
                     if (typeof d === 'string' && d.length >= 10) return d.substring(0, 10);
                     return d; 
                 };
                 val = { ...val, [fromKey]: parseDateObj(from), [toKey]: parseDateObj(to) };
             }
             else if (f.type === 'Number') val = Number(val);
             
             tempDefaults[f.field] = val; 
         }
      });

      if (isMounted) {
         setResolvedDefaults(tempDefaults);
         
         const urlFilters = getUrlFilters();

         let savedFilters = {};
         if (!isWidget) {
            try {
                const stored = localStorage.getItem(filterStorageKey);
                if (stored) savedFilters = JSON.parse(stored);
            } catch (e) {
                console.error("Lỗi đọc filter từ localStorage:", e);
            }
         }

         const mergedData = currentDrillDownCondition?{ ...tempDefaults, ...(defaultCondition || {}), ...currentDrillDownCondition}:{ ...tempDefaults, ...(defaultCondition || {}), ... savedFilters, ...urlFilters };
         //console.log("[mergedData]",{mergedData,currentDrillDownCondition,drillDownCondition});
         setInitialMergedData(mergedData);
         //Xoá điều kiện drillDown để tránh áp dụng khi user thay đổi điều kiện lọc trong form filter
         setCurrentDrillDownCondition(null);

         const hasUrlData = Object.keys(urlFilters).length > 0;
         const hasPropsDefault = defaultCondition && Object.keys(defaultCondition).length > 0;
         const hasSavedFilters = Object.keys(savedFilters).length > 0;
         const hasDrillDownCondition = currentDrillDownCondition && Object.keys(currentDrillDownCondition).length > 0;
         //const hasFieldDefault = Object.keys(tempDefaults).length > 0;
         
         const isAllRequiredFilled = checkHasAllRequiredFields(mergedData);
         //const shouldAutoApply = isAllRequiredFilled && (isWidget || hasUrlData || hasPropsDefault || hasFieldDefault || hasSavedFilters);
         const shouldAutoApply = isAllRequiredFilled && (isWidget || hasDrillDownCondition);

         
         if (shouldAutoApply) {
             setAppliedCondition(mergedData);
             setShowFilter(false);
         } else {
             setAppliedCondition(null);
             setShowFilter(!isWidget);
         }

         setIsDefaultsReady(true);
      }
    };

    setIsDefaultsReady(false); 
    initDefaults();

    return () => { isMounted = false; };
  }, [initFingerprint, isWidget, filterStorageKey]);

  const handleApplyFilter = (newCondition) => {
    //console.log("[reportFrame][handleApplyFilter]",newCondition);
    setAppliedCondition(newCondition);
    setShowFilter(false); 
    
    if (!isWidget) {
        try {
            localStorage.setItem(filterStorageKey, JSON.stringify(newCondition));
        } catch (e) {
            console.error("Lỗi lưu filter vào localStorage:", e);
        }
    }
  };

  // ================= 4. ENGINE SINH ĐIỀU KIỆN QUERY =================
  const finalQueryCondition = useMemo(() => {
    if (!appliedCondition || !isDefaultsReady) return null; 
    const finalQuery = {};
    Object.keys(appliedCondition).forEach(key => {
        const val = appliedCondition[key];
        const fieldDef = finalFilterFields?.find(f => f.field === key);
        if (val !== undefined && val !== null && val !== '') {
            if (fieldDef && fieldDef.type === 'DateFilter' && typeof val === 'object') {
                const fromKey = fieldDef.mapTo?.[0] || 'fromDate';
                const toKey = fieldDef.mapTo?.[1] || 'toDate';
                if (val[fromKey]) finalQuery[fromKey] = val[fromKey];
                if (val[toKey]) finalQuery[toKey] = val[toKey];
            } 
            else if (Array.isArray(val)) { if (val.length > 0) finalQuery[key] = val; } 
            else if (typeof val === 'object' && !val.getTime) {
                const cleanedObj = {};
                Object.keys(val).forEach(subKey => { if (val[subKey] !== '' && val[subKey] !== null && val[subKey] !== undefined) cleanedObj[subKey] = val[subKey]; });
                if (Object.keys(cleanedObj).length > 0) finalQuery[key] = cleanedObj; 
            }
            else finalQuery[key] = typeof val === 'string' ? val.trim() : val;
        }
    });
    return finalQuery;
  }, [appliedCondition, finalFilterFields, isDefaultsReady]);

  // CỜ XÁC ĐỊNH CHẾ ĐỘ PIVOT ĐỂ NẠP VÀO REACT QUERY
  const isPivotMode = viewMode === 'pivot' || isWidget;

  // ================= 5. REACT QUERY =================
  const { 
    data: infiniteData, isLoading, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage, refetch, isFetching
  } = useInfiniteQuery({
    // THÊM isPivotMode VÀO QUERY KEY ĐỂ TRIGGER REFETH KHI CHUYỂN CHẾ ĐỘ
    queryKey: ['report', context.appInfo?._id, code, finalQueryCondition, { limit: currentLimit, isPivot: isPivotMode }], 
    initialPageParam: { page: 1, id_session: null }, 
    queryFn: async ({ pageParam }) => {
        let payload = { ...finalQueryCondition };
       
        //dynamic condition
        let handle_condition =  handleConditionScript;
        if(handle_condition){
            const string_func = `return (async ()=>{
                ${handle_condition}
            })()`;
            const func = new Function("Moment","moment","utils","context","condition",string_func);
            payload = await func(moment,moment,utils,context,payload);
            if(!payload) throw "Hàm xử lý điều kiện lọc cần trả về một đối tượng";
        }
         //NẾU ĐANG LÀ PIVOT -> KHÔNG PHÂN TRANG
        if (finalEnableLoadMore && !isPivotMode) {
            if(payload.pagination===undefined) payload.pagination = 1;
            payload.limit = payload.limit || currentLimit;
            payload.page = pageParam.page;
            if (pageParam.id_session) payload.id_session = pageParam.id_session;
        } else {
            payload.pagination = 0; // Kéo toàn bộ dữ liệu 1 lượt
        }
       //console.log("[reportframe] fetch data:",payload)

        let api_code = finalApiCode;
        if (api_code.indexOf("return ") >= 0) {
            if (!apiCache.has(api_code)) {
                const string_func = `return (async ()=>{
                    ${api_code}
                })()`;
                const compiledFunc = new Function("Moment", "moment", "utils", "context", "user", "condition", string_func);
                apiCache.set(api_code, compiledFunc);
            }
            const func = apiCache.get(api_code);
            api_code = await func(moment, moment, utils, context, context.userInfo, finalQueryCondition);
            if (!api_code) throw "API Báo cáo không có giá trị";
            api_code = api_code.toLowerCase();
        }
        let report_info_code = api_code!=code?code:null;
        const response = await context.apis.asyncGetReport(userToken, api_code, payload,"POST",report_info_code);

        let handleData;
        if(handleDataScript){
            const string_func = `return (async ()=>{
                ${handleDataScript}
            })()`;
            try{
                handleData = new Function("data","utils","context","condition",string_func);
            }catch(e){
                console.error(e,string_func);
            }
        }
        
        if (finalEnableLoadMore && !isPivotMode) {
            let rawData = response;
            if(handleData){
                if(handleDataScript.includes("data.rows")){
                    rawData ={rows: rawData?.rows || rawData?.data||rawData||[]}
                }else if(handleDataScript.includes("data.data")){
                    rawData ={data: rawData?.rows || rawData?.data||rawData||[]}
                } else {
                    rawData = rawData?.rows || rawData?.data||rawData||[];
                }
                rawData = await handleData(rawData,utils,context,finalQueryCondition)
            }
            rawData = rawData?.data || rawData || [];
            if (finalProcessData && typeof finalProcessData === 'function') rawData = finalProcessData(rawData);
            return { 
                id_session: response?.id_session,
                data: rawData, 
                pagination: response?.pagination || { page: 1, totalPages: 1 }, 
                summary: response?.summary,
                rawResponse: response 
            };
        } else {
            let rawData = response;
            if(handleData){
                rawData = await handleData(rawData,utils,context,finalQueryCondition)
                console.log("[ReportFrame][handleData]...",{rawData,handleDataScript})
            }
            rawData = rawData?.data || rawData || [];
            if (finalProcessData && typeof finalProcessData === 'function') rawData = finalProcessData(rawData);
            return { data: rawData, summary: response?.summary, rawResponse: response }; 
        }
    },
    getNextPageParam: (lastPage) => {
      // HỦY LOAD NEXT PAGE NẾU ĐANG LÀ PIVOT HOẶC KHÔNG CHO PHÉP LOAD MORE
      if (!finalEnableLoadMore || isPivotMode) return undefined;
      const pagination = lastPage?.pagination;
      if (pagination && pagination.page < pagination.totalPages) return { page: pagination.page + 1, id_session: lastPage.id_session };
      return undefined; 
    },
    // CHỈ gọi API khi đã đủ điều kiện: có user thật, ĐÃ CHỌN CÔNG TY và không đang trong bước xác
    // thực. Bản cũ chỉ kiểm `!isPublicUser` nên ngay sau khi đăng nhập (chưa chọn công ty) là bắn
    // request với id_app rỗng -> lỗi khó hiểu, rồi lại tự gọi lại khi có công ty.
    enabled: finalQueryCondition !== null && isDefaultsReady && !!userToken && authReady.canFetch,   
    // Widget dashboard: dữ liệu coi là còn mới trong suốt chu kỳ tự làm mới (5–10 phút) và KHÔNG chạy
    // lại khi người dùng quay lại tab. Bản cũ: staleTime 30s + refetchOnWindowFocus -> mỗi lần quay lại
    // tab dashboard là MỌI widget cùng gọi lại báo cáo một lúc -> reportserver vọt CPU từng đợt.
    // Muốn số liệu mới ngay thì dùng nút làm mới (refreshTrigger -> refetch() bỏ qua staleTime).
    staleTime: isWidget ? (refreshInterval > 0 ? Math.max(refreshInterval - 1000, 30 * 1000) : 5 * 60 * 1000) : 30 * 1000, 
    refetchInterval: refreshInterval > 0 ? refreshInterval : false, 
    refetchOnWindowFocus: false, 
  });

  const reportData = (finalEnableLoadMore && !isPivotMode)
    ? infiniteData?.pages.flatMap(page => page.data || []) || []
    : infiniteData?.pages[0]?.data || infiniteData?.pages[0] || []; 
  
  //Tạo cho mỗi row một __key
  let time_key = new Date().getTime();
  reportData.forEach((r,index)=>{
    if(typeof(r)=="object"){
        r.__key = `${index}-${time_key}`
    }
  })

  const reportSummary = infiniteData?.pages[0]?.summary || null;
  const id_session = infiniteData?.pages[0]?.id_session;
  const rawFirstPageResponse = infiniteData?.pages[0]?.rawResponse || {};
  const totalRecords = infiniteData?.pages[0]?.pagination?.total || reportData.length;
  const _currentLimit = infiniteData?.pages[0]?.pagination?.limit || currentLimit;

  const authGate = useAuthGate(requireLogin, { error });
  const isShowingLockScreen = authGate.blocked;
  const { scrollRef, saveScroll } = useScrollRestoration({
    key: `${location.pathname}_report_${code}`, 
    isReady: !isLoading && infiniteData !== undefined && !isError,
    condition: !isWidget && !isShowingLockScreen 
  });
  useImperativeHandle(ref, () => ({
    getReportData:()=>{
        return reportData||[];
    },
    refresh: () => { if (refetch) refetch(); },
    openFilter: () => { setShowFilter(true); },
    // Điều kiện ĐANG áp dụng (dạng của form lọc) — widget dashboard dùng để mở trang báo cáo gốc cùng điều kiện.
    getCondition: () => appliedCondition || initialMergedData || {}
  }), [refetch, setShowFilter, appliedCondition, initialMergedData]);

  // ================= 6. ENGINE DYNAMIC PIVOT COLUMNS =================
const dynamicDisplayFields = useMemo(() => {
    let expandedCols = [];
    (finalDisplayFields || []).forEach(col => {
        if (col.subColumns && col.subColumns.data) {
            const auxDataArray = rawFirstPageResponse[col.subColumns.data] || [];
            auxDataArray.forEach(item => {
                let c = {
                    ...col,
                    subColumns: undefined,
                    source_field: col.field, // NEW: lưu field gốc của cột Pivot để đối chiếu với rptInfo khi export
                }
                if(typeof item=="object"){
                    if(col.subColumns.code_field){
                        c.field = item[col.subColumns.code_field] || item;
                        c.label = item[col.subColumns.code_label || col.subColumns.code_field]
                    }else{
                        c.field = item;
                        c.label = item;
                    }
                }else{
                   c.field = item;
                   c.label = item;
                }
                expandedCols.push(c);
            });
        } else {
            expandedCols.push(col);
        }
    });
    return expandedCols;
}, [finalDisplayFields, rawFirstPageResponse]);

  // ================= 7. STATE ẨN/HIỆN CỘT =================
  const [showColumnDrawer, setShowColumnDrawer] = useState(false);
  const colStorageKey = `flexbiz_hidden_cols_${code}`; 
  
  const [hiddenCols, setHiddenCols] = useState(() => {
    try {
        const stored = localStorage.getItem(colStorageKey);
        return stored ? JSON.parse(stored) : [];
    } catch (e) { return []; }
  });

  const handleToggleColumn = (field) => {
    setHiddenCols(prev => {
        const newHidden = prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field];
        localStorage.setItem(colStorageKey, JSON.stringify(newHidden));
        return newHidden;
    });
  };

  const handleResetColumns = () => {
    setHiddenCols([]);
    localStorage.removeItem(colStorageKey);
  };
  const visibleDisplayFields = useMemo(() => {
    return dynamicDisplayFields.filter(col => !hiddenCols.includes(col.field) && !isHiddenColumn(col,finalQueryCondition));
  }, [dynamicDisplayFields, hiddenCols,finalQueryCondition]);

  // ================= 8. SỰ KIỆN CLICK (DRILL-DOWN) =================
  const hasRowLevelAction = useMemo(() => {
    return reportData.some(row => row.drilldown_code || (row.id_ct && (row.page_code || row.ma_ct)));
  }, [reportData]);

  const isClickable = !!onDrillDown || !!finalDrillDownCode || !!finalDrillDownScript || hasRowLevelAction;

  //console.log("[report] row isClickable",isClickable);

  const handleRowClick = isClickable ? async (row) => {
    //Ưu tiên drilldown mở chứng từ
    if (row.id_ct && (row.page_code || row.ma_ct)) {
        let targetApiCode = row.page_code || row.ma_ct;
        //cần fetch voucher gốc 
        if(row.ma_ct){
            try{
                const voucher = await context.apis.asyncGetData(context.userInfo.token,row.ma_ct.toLowerCase(),{_id:row.id_ct});
                targetApiCode = voucher?.listinfo_code || targetApiCode;
            }catch(e){
                console.error("[ReportFrame] không thể lấy dữ liệu gốc",{ma_ct:row.ma_ct,id_ct:row.id_ct}) 
            }
            
        }
        openEdit(targetApiCode, null, null, {_id:row.id_ct});
        return;
    }
    //console.log("[report] row click",row);
    if (onDrillDown) {
        onDrillDown(row, appliedCondition);
        return;
    }
    if (finalDrillDownScript) {
        try {
            let func = new Function("row", "condition", "Moment", "moment", (finalDrillDownScript.indexOf("return ") < 0 ? "return " : "") + finalDrillDownScript);
            let drilldownObj = func(row, appliedCondition, moment, moment);
            if (drilldownObj) {
                if (typeof(drilldownObj) === "string") return window.open(drilldownObj);
                if (drilldownObj.code && drilldownObj.type === "link") return window.open(drilldownObj.code);
                
                drilldownObj.condition = { ...appliedCondition,...row, ...drilldownObj.condition };
                openReport(drilldownObj.code.toLowerCase(), null, null, null, drilldownObj.condition);
                return;
            }
        } catch (e) { console.error("Lỗi khi chạy script drilldown:", e); }
    }

    const targetDrillDownCode = row.drilldown_code || finalDrillDownCode;
    if (targetDrillDownCode) {
        const mergedCondition = { ...appliedCondition, ...row };
        openReport(targetDrillDownCode, null, null, null, mergedCondition);
        return;
    }
  } : undefined;

  // CAN THIỆP CHẶN CUỘC GỌI NEXT PAGE ĐỒNG THỜI CHỐNG LỖI MẤT SESSION
  const handleLoadMore = () => { 
      if (hasNextPage && !isFetchingNextPage && !isFetching) {
          fetchNextPage(); 
      }
  };

  // ================= RENDER =================
  if (isShowingLockScreen) {
    return (
      <Box sx={{ height: '100dvh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default', ...containerSx }}>
          {!isWidget && <ReportHeader title={finalTitle} code={code} onBack={onBack}/>}
          <Box sx={{flex:1}}><LockScreen {...authGate.lockProps}/></Box>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100dvh', bgcolor: isWidget ? 'transparent' : 'background.paper', borderRadius: isWidget ? 0 : 3, overflow: 'hidden', ...containerSx }}>
        {!isWidget && (
          <ReportHeader 
            title={finalTitle} 
            code={code} 
            onBack={onBack} 
            onOpenFilter={() => setShowFilter(true)} 
            onOpenColumnSettings={() => setShowColumnDrawer(true)} 
            viewMode={viewMode} 
            onViewModeChange={!renderReport ? handleViewModeChange : null} 
            enableCustomView={!renderReport}
            onOpenCustomView={() => setOpenCustomViewEditor(v => v + 1)}
            onRefresh={() => refetch()} 
            onPrint={() => setShowPrintDialog(true)}
            isRefreshing={isFetching && !isLoading && !isFetchingNextPage}
        />
        )}
        
        <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative', pb: 'env(safe-area-inset-bottom)', ...contentSx }}>
            <Box sx={{ p:0, flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              
              {/* === THAY THẾ CIRCULAR PROGRESS BẰNG SKELETON === */}
              {!isDefaultsReady ? (
                  <ReportSkeleton viewMode={viewMode} isWidget={isWidget} />
              ) : appliedCondition === null ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'text.secondary' }}><Typography>Vui lòng chọn điều kiện và bấm "Xem báo cáo"</Typography></Box>
              ) : isLoading ? ( 
                  <ReportSkeleton viewMode={viewMode} isWidget={isWidget} />
              ) : isError ? (
                  <Typography color="error" sx={{ p: 2, fontSize: isWidget ? '0.85rem' : '1rem' }}>{error?.message || error?.error|| error || "Lỗi tải dữ liệu báo cáo."}</Typography>
              ) : (
                  <Box sx={{ mt: 0, flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    {renderReport 
                        ? renderReport(reportData, { onLoadMore: handleLoadMore, isFetchingMore: isFetchingNextPage, hasNextPage: hasNextPage, summary: reportSummary }) 
                        : <>
                            {isPivotMode ? (
                            <DataPivotViewer 
                                data={reportData} 
                                columns={visibleDisplayFields} 
                                reportCode={code}
                                apiCode={finalApiCode}
                                reportCondition={appliedCondition || initialMergedData}
                                filterFields={finalFilterFields}
                                handleDataScript={handleDataScript}
                                initialConfigId={initialPivotConfigId}
                            />
                            ) : (
                            visibleDisplayFields?.length==0?
                                reportData.map((r,index)=><Typography key={index.toString()} dangerouslySetInnerHTML={{__html:(typeof(r)=="object"?JSON.stringify(r,null,2):(r||""))}} />)
                            :
                            <ReportDataViewer 
                                data={reportData}
                                condition={appliedCondition || initialMergedData}
                                context={context}
                                columns={visibleDisplayFields}
                                viewMode={viewMode} 
                                renderCardItem={renderCardItem} 
                                renderTableRow={renderTableRow} 
                                onViewModeChange={handleViewModeChange} 
                                hasNextPage={hasNextPage}
                                onLoadMore={handleLoadMore}
                                totalRecords={totalRecords}
                                currentLimit={_currentLimit}
                                onLimitChange={setCurrentLimit}
                                isFetching={isFetchingNextPage}
                                onRowClick={handleRowClick} 
                                ref={scrollRef}
                                onScroll={saveScroll}
                                onRefresh={() => refetch()} 
                                reportCode={code}
                                apiCode={finalApiCode}
                                filterFields={finalFilterFields}
                                enableCustomView={!isWidget}
                                openScriptEditorSignal={openCustomViewEditor}
                            />
                            )}
                        </>
                    }
                  </Box>
              )}
            </Box>
        </Box>

        {!isWidget && isDefaultsReady && (
            <FilterDrawer 
                open={showFilter} 
                onClose={() => setShowFilter(false)} 
                filterFields={finalFilterFields} 
                searchLabel={searchLabel}
                cancelLabel={cancelLabel}
                currentData={appliedCondition || initialMergedData} 
                defaultData={initialMergedData} 
                onApply={handleApplyFilter} 
            />
        )}

        {!isWidget && (
            <ReportColumnDrawer 
                open={showColumnDrawer} 
                onClose={() => setShowColumnDrawer(false)} 
                columns={dynamicDisplayFields} 
                hiddenCols={hiddenCols} 
                onToggle={handleToggleColumn} 
                onReset={handleResetColumns} 
            />
        )}
        
        {showPrintDialog && (
            <PrintActionDrawer 
                open={showPrintDialog}
                onClose={() => setShowPrintDialog(false)}
                code={code}
                apiCode={finalApiCode} 
                mode="report"
                title={finalTitle} 
                filterFields={filterFields}
                columns={visibleDisplayFields}
                searchCondition={{...finalQueryCondition, id_session}} 
            />
        )}

    </Box>
  );
});

// =====================================================================
// BỌC BẰNG REACT.MEMO CHẶN RENDER VÔ ÍCH NẾU THAM SỐ CHỈ LÀ DIALOG
// =====================================================================
const ReportFrameMemo = React.memo(ReportFrameRender, (prevProps, nextProps) => {
  const prevSearch = getCleanSearchString(prevProps.location?.search);
  const nextSearch = getCleanSearchString(nextProps.location?.search);
  
  if (prevSearch !== nextSearch) return false;
  if (prevProps.location?.pathname !== nextProps.location?.pathname) return false;

  const prevKeys = Object.keys(prevProps);
  const nextKeys = Object.keys(nextProps);
  
  if (prevKeys.length !== nextKeys.length) return false;

  for (let key of prevKeys) {
    if (key === 'location') continue; 
    if (prevProps[key] !== nextProps[key]) return false;
  }
  return true;
});

// =====================================================================
// COMPONENT WRAPPER ĐỂ XUẤT RA NGOÀI
// =====================================================================
export default forwardRef(function ReportFrame(props, ref) {
  const location = useLocation();
  //console.log("default condition",props.defaultCondition);
  return <ReportFrameMemo {...props} location={location} ref={ref} />;
});